import "../utils/mui-display-names"; // Fix MUI component names in React DevTools
import { Box, CircularProgress } from "@mui/material";
import { NuqsAdapter } from "nuqs/adapters/react-router/v7";
import { lazy, Suspense } from "react";
import { HotkeysProvider } from "react-hotkeys-hook";
import { createBrowserRouter, Route, RouterProvider, Routes } from "react-router-dom";
import { AuthProvider } from "../context/AuthContext";
// Core components - loaded immediately (including landing page)
import { App } from "./app";
import { LoginForm } from "./auth/LoginForm";
import { BoardStateProvider } from "./boardv2/boardState";
import { RuntimeErrorTrigger } from "./dev/runtime-error-trigger";
import { GlobalStateProvider } from "./globalState";
import LogPageViews from "./LogPageViews";
import { DataDeletionConfirmPage } from "./legal/DataDeletionConfirmPage";
import { DataDeletionRequestPage } from "./legal/DataDeletionRequestPage";
import { DataProcessingAgreement } from "./legal/DataProcessingAgreement";
import { PrivacyPolicy } from "./legal/PrivacyPolicy";
import { TermsOfService } from "./legal/TermsOfService";
import { LandingPage } from "./public/LandingPage";
import { RootErrorBoundary, RouteErrorBoundary } from "./RootErrorBoundary";
import { Todo } from "./todo";

// Lazy-loaded components - only load when needed
const RegisterForm = lazy(() =>
	import("./auth/RegisterForm").then((m) => ({ default: m.RegisterForm }))
);
const ProviderCallback = lazy(() =>
	import("./auth/ProviderCallback").then((m) => ({
		default: m.ProviderCallback,
	}))
);
const ForgotPasswordForm = lazy(() =>
	import("./auth/ForgotPasswordForm").then((m) => ({
		default: m.ForgotPasswordForm,
	}))
);
const ResetPasswordForm = lazy(() =>
	import("./auth/ResetPasswordForm").then((m) => ({
		default: m.ResetPasswordForm,
	}))
);
const Grading = lazy(() => import("./grading").then((m) => ({ default: m.Grading })));
const EmailView = lazy(() => import("./email/email-view"));
const CvBrowser = lazy(() =>
	import("./storage/cv-browser").then((m) => ({ default: m.CvBrowser }))
);
const CandidatePool = lazy(() =>
	import("./candidate-pool/candidate-pool").then((m) => ({
		default: m.CandidatePool,
	}))
);
const CompanySearch = lazy(() =>
	import("./search").then((m) => ({ default: m.CompanySearch }))
);
const Config = lazy(() => import("./config").then((m) => ({ default: m.Config })));
const CompanySales = lazy(() =>
	import("./sales/company-sales").then((m) => ({ default: m.CompanySales }))
);
const PersonSearch = lazy(() =>
	import("./search/person-search").then((m) => ({ default: m.PersonSearch }))
);
const ProdImportPage = lazy(() =>
	import("./import/prod-import-page").then((m) => ({
		default: m.ProdImportPage,
	}))
);
const StagingDeployView = lazy(() => import("./staging-deploy/staging-deploy-view"));
const StagingOverviewView = lazy(
	() => import("./staging-deploy/staging-overview-view")
);
const PersonView = lazy(() =>
	import("./person").then((m) => ({ default: m.PersonView }))
);
const CompanyView = lazy(() =>
	import("./company").then((m) => ({ default: m.CompanyView }))
);
const ChartView = lazy(() =>
	import("./analytics/chart-view").then((m) => ({ default: m.ChartView }))
);
const StockChartView = lazy(() =>
	import("./analytics/stockchart-view").then((m) => ({ default: m.StockChartView }))
);
const BoardView = lazy(() =>
	import("./boardv2").then((m) => ({ default: m.BoardView }))
);
const BotBoard = lazy(() =>
	import("./analytics/bot-board").then((m) => ({ default: m.BotBoard }))
);
const BotView = lazy(() => import("./analytics/bot-view"));
const DatabaseLoad = lazy(() => import("./analytics/database-load"));
const AdvisoryLocks = lazy(() => import("./analytics/advisory-locks"));
const DatabaseSize = lazy(() => import("./analytics/database-size"));
const HotDatabaseTables = lazy(() => import("./analytics/hot-database-tables"));
const SendInviteQueue = lazy(() => import("./analytics/send-invite-queue"));
const InmailCostQueue = lazy(() => import("./analytics/inmail-cost-queue"));
const ContactInfoTngQueue = lazy(() => import("./analytics/contactinfo-tng-queue"));
const KpisPage = lazy(() =>
	import("./statistics/kpis-page").then((m) => ({ default: m.KpisPage }))
);
const PersonAnalysis = lazy(() => import("./analytics/person-analysis"));
const AssignmentStats = lazy(() => import("./statistics/assignment_stats"));
const AssignmentCharts = lazy(() => import("./statistics/assignment-charts"));
const Job = lazy(() => import("./job_listing/job"));
const MlStats = lazy(() => import("./analytics/ml-stats"));
const RerankJudgeView = lazy(() => import("./analytics/rerank-judge-view"));
const LLMPerformance = lazy(() => import("./analytics/llm-performance"));
const LLMPricing = lazy(() => import("./analytics/llm-pricing"));
const DataExplorer = lazy(() => import("./analytics/data-explorer"));
const DocsIndexPage = lazy(() => import("./docs/docs-index-page"));
const DocsPage = lazy(() => import("./docs/docs-page"));
const WorkflowListPage = lazy(() => import("./workflow/workflow-list-page"));
const WorkflowDetailPage = lazy(() => import("./workflow/workflow-detail-page"));
const WorkflowProcessPage = lazy(() => import("./workflow/workflow-process-page"));
const WorkflowDocumentReader = lazy(
	() => import("./workflow/workflow-document-reader")
);
const AiLabView = lazy(() => import("./ai-lab/ai-lab-view"));
const ReleaseNotesPage = lazy(() => import("./release-notes/release-notes-page"));
const SupportListView = lazy(() => import("./support/support-list-view"));
const SupportThreadView = lazy(() => import("./support/support-thread-view"));
const TechDocsIndexPage = lazy(() => import("./tech-docs/tech-docs-index-page"));
const TechDocsPage = lazy(() => import("./tech-docs/tech-docs-page"));
const GitStats = lazy(() => import("./analytics/git-stats"));
const OkrDashboard = lazy(() => import("./analytics/okr-dashboard"));
const CeleryTasks = lazy(() => import("./analytics/celery-tasks"));
const LinkedinJobPosting = lazy(() => import("./job_listing/linkedinjobposting"));
const PendingUsers = lazy(() => import("./admin/PendingUsers"));
const ActiveUserManagement = lazy(() => import("./admin/ActiveUserManagement"));
const CustomerLicensesView = lazy(() => import("./admin/customer-licenses-view"));
const DeletionRequestsView = lazy(() => import("./admin/deletion-requests-view"));
const LLMPromptTemplates = lazy(() => import("./analytics/llm-prompt-templates"));
const PersonTextView = lazy(() =>
	import("./person/person-text-view").then((m) => ({ default: m.PersonTextView }))
);
const LatestPersonRedirect = lazy(() =>
	import("./person/latest-person-redirect").then((m) => ({
		default: m.LatestPersonRedirect,
	}))
);
const RandomPersons = lazy(() => import("./llm/RandomPersons"));
const LatestPersons = lazy(() => import("./analytics/latest-persons"));
const AccountsView = lazy(() => import("./analytics/accounts-view"));
const LatestVoyagerLogs = lazy(() => import("./analytics/latest-voyager-logs"));
const VoyagerLogStats = lazy(() => import("./analytics/voyager-log-stats"));
const LLMCacheStats = lazy(() => import("./analytics/llm-cache-stats"));
const GpqTabsWrapper = lazy(() => import("./analytics/gpq-tabs-wrapper"));
const ExtractorPrompts = lazy(() => import("./analytics/extractor-prompts"));
const LLMEvaluation = lazy(() => import("./analytics/llm-evaluation"));
const AssignmentLLMAgreement = lazy(
	() => import("./analytics/assignment-llm-agreement")
);
const VoteTestsetList = lazy(() => import("./llm/VoteTestsetList"));
const LLMEvaluationBatchList = lazy(() => import("./llm/LLMEvaluationBatchList"));
const HumanOperatorAgreement = lazy(
	() => import("./analytics/human-operator-agreement")
);
const AssignmentLLMSkillsList = lazy(() => import("./llm/AssignmentLLMSkillsList"));
const GradeConnectQueues = lazy(() => import("./analytics/grade-connect-queues"));
const GradeConnectQueueItems = lazy(() => import("./analytics/GradeConnectQueueItems"));
const LLMCacheList = lazy(() => import("./llm/LLMCacheList"));
const ProviderBatchJobsView = lazy(() => import("./llm/provider-batch-jobs-view"));
const AssignmentsOverviewView = lazy(() => import("./admin/assignments-overview-view"));
const EmailSync = lazy(() => import("./analytics/EmailSync"));
const AnalyticsDashboard = lazy(() => import("./analytics/AnalyticsDashboard"));
const LokiLogs = lazy(() => import("./analytics/loki-logs"));
const PydollScreenshotMonitor = lazy(
	() => import("./analytics/pydoll-screenshot-monitor")
);
const CaptchaMonitor = lazy(() => import("./analytics/captcha-monitor"));
const EmailJournalView = lazy(() => import("./devtools/email-journal-view"));
const PostfixDeliveryEventsView = lazy(
	() => import("./devtools/postfix-delivery-events")
);
const ScreenTilesView = lazy(() => import("./devtools/screen-tiles-view"));
const IndexHealthView = lazy(() => import("./analytics/index-health"));
const AssignmentVotesView = lazy(() => import("./votes/AssignmentVotesView"));
const AssignmentFunnelView = lazy(() => import("./funnel/AssignmentFunnelView"));
const CandidatesDiagram = lazy(() => import("./candidates/candidates-diagram"));
const LicKeysMatrixView = lazy(() => import("./analytics/lic-keys"));
const PluginWorkLogView = lazy(() =>
	import("./plugin-work-log/PluginWorkLogView").then((m) => ({
		default: m.default,
	}))
);
const CookieDebugView = lazy(() => import("./cookie-debug/index"));
const FingerprintSessionList = lazy(() => import("./fingerprint-sessions/index"));
const FingerprintSessionDetail = lazy(
	() => import("./fingerprint-sessions/session-detail")
);
const AssignmentLocationsView = lazy(
	() => import("./analytics/assignment-locations-view")
);
const OstmdDetailView = lazy(() => import("./analytics/ostmd-detail-view"));
const RotatingProxiesView = lazy(() => import("./analytics/rotating-proxies"));
const DbBackups = lazy(() => import("./analytics/db-backups"));
const ElasticsearchStatusView = lazy(() => import("./analytics/elasticsearch-status"));
const JobylonStatusView = lazy(() => import("./analytics/jobylon-status"));
const SesDashboard = lazy(() => import("./ses-dashboard/ses-dashboard"));
const ThemeColorsView = lazy(() => import("./debug/theme-colors-view"));
const InfraCostsView = lazy(() => import("./analytics/infra-costs-view"));
const ExternalReviewPage = lazy(() => import("./external-review/external-review-page"));
const SystemSettingsConfig = lazy(() =>
	import("./config/SystemSettingsConfig").then((m) => ({
		default: m.SystemSettingsConfig,
	}))
);

// Loading fallback component
const PageLoader = () => (
	<Box
		data-component="page-loader"
		display="flex"
		justifyContent="center"
		alignItems="center"
		minHeight="100vh"
	>
		<CircularProgress />
	</Box>
);

const RootLayout = () => (
	<RootErrorBoundary>
		<NuqsAdapter>
			<HotkeysProvider initiallyActiveScopes={["grading", "*"]}>
				<AuthProvider>
					<LogPageViews />
					<Suspense fallback={<PageLoader />}>
						<Routes>
							{/* Public landing page - NO AUTH REQUIRED */}
							<Route path="/" element={<LandingPage />} />

							{/* Auth routes - publicly accessible */}
							<Route path="/login" element={<LoginForm />} />
							<Route path="/register" element={<RegisterForm />} />
							{/* Where Google/Microsoft send the browser back to */}
							<Route
								path="/login/oauth/:provider"
								element={<ProviderCallback />}
							/>
							<Route
								path="/forgot-password"
								element={<ForgotPasswordForm />}
							/>
							<Route
								path="/reset-password/:uid/:token"
								element={<ResetPasswordForm />}
							/>

							{/* External review - public, no auth */}
							<Route
								path="/review/:token"
								element={<ExternalReviewPage />}
							/>

							{/* Legal routes - publicly accessible */}
							<Route path="/privacy-policy" element={<PrivacyPolicy />} />
							<Route
								path="/data-deletion"
								element={<DataDeletionRequestPage />}
							/>
							<Route
								path="/data-deletion/confirm/:token"
								element={<DataDeletionConfirmPage />}
							/>
							<Route
								path="/terms-of-service"
								element={<TermsOfService />}
							/>
							<Route
								path="/data-processing-agreement"
								element={<DataProcessingAgreement />}
							/>
							{/* Authenticated application routes - ALL require auth */}
							<Route element={<App />}>
								<Route path="todo" element={<Todo />} />
								<Route path="email" element={<EmailView />} />

								{/* Docs (staff-only — gated server-side via /api/v2/docs) */}
								<Route path="docs">
									<Route index element={<DocsIndexPage />} />
									<Route path=":slug" element={<DocsPage />} />
								</Route>

								{/* AI Lab (staff-only — gated in the component) */}
								<Route path="ai-lab" element={<AiLabView />} />

								{/* Release notes (staff-only — gated server-side too) */}
								<Route
									path="release-notes"
									element={<ReleaseNotesPage />}
								/>

								{/* Support tickets — the staff queue. Reporters
								    read and write their own conversations in the
								    bubble's panel, which is on every page. */}
								<Route path="support">
									<Route index element={<SupportListView />} />
									<Route
										path=":ticketId"
										element={<SupportThreadView />}
									/>
								</Route>

								{/* Ticket workflow (Claude Code cloud sessions) */}
								<Route path="workflow">
									<Route index element={<WorkflowListPage />} />
									<Route
										path="process"
										element={<WorkflowProcessPage />}
									/>
									<Route
										path=":ticketId"
										element={<WorkflowDetailPage />}
									/>
									<Route
										path=":ticketId/document/:versionId"
										element={<WorkflowDocumentReader />}
									/>
								</Route>

								{/* Technical docs (staff-only) — splat slug for nested
								    paths like /tech-docs/api/persons */}
								<Route path="tech-docs">
									<Route index element={<TechDocsIndexPage />} />
									<Route path="*" element={<TechDocsPage />} />
								</Route>

								{/* Votes routes */}
								<Route path="votes">
									<Route
										path="assignment/:assignmentId"
										element={<AssignmentVotesView />}
									/>
								</Route>

								{/* Funnel routes */}
								<Route path="funnel">
									<Route
										path="assignment/:assignmentId"
										element={<AssignmentFunnelView />}
									/>
								</Route>

								{/* Candidates Pipeline Diagram */}
								<Route
									path="candidates/:assignmentId"
									element={<CandidatesDiagram />}
								/>

								{/* User Management routes */}
								<Route
									path="user-management/pending-users"
									element={<PendingUsers />}
								/>
								<Route
									path="user-management/active-users"
									element={<ActiveUserManagement />}
								/>
								<Route
									path="user-management/customer-licenses"
									element={<CustomerLicensesView />}
								/>
								<Route
									path="user-management/deletion-requests"
									element={<DeletionRequestsView />}
								/>

								<Route path="cv-browser" element={<CvBrowser />} />

								<Route
									path="assignment_stats/:assignmentId"
									element={
										<BoardStateProvider>
											<AssignmentStats />
										</BoardStateProvider>
									}
								/>

								{/* Board v2 */}
								<Route path="board">
									<Route
										path="v2"
										element={
											<BoardStateProvider>
												<BoardView />
											</BoardStateProvider>
										}
									></Route>
								</Route>

								<Route
									path="candidate-pool"
									element={<CandidatePool />}
								/>

								{/* Grading with nested routes */}
								<Route path="grading">
									<Route path=":assignmentId" element={<Grading />}>
										<Route path=":personId" element={<Grading />} />
									</Route>
								</Route>
								<Route
									path="company_search"
									element={<CompanySearch />}
								/>
								<Route
									path="company_sales"
									element={<CompanySales />}
								/>

								<Route
									path="person_search"
									element={<PersonSearch />}
								/>

								<Route
									path="prod_import"
									element={<ProdImportPage />}
								/>

								<Route
									path="staging-deploy"
									element={<StagingDeployView />}
								/>

								<Route
									path="staging-overview"
									element={<StagingOverviewView />}
								/>

								{/* Config routes */}
								<Route path="config" element={<Config />}>
									<Route path="settings" element={<Config />} />
									<Route path="profile" element={<Config />} />
									<Route path="features" element={<Config />} />
									<Route path="linkedin" element={<Config />} />
									<Route path="email" element={<Config />} />
									<Route
										path="email/:provider"
										element={<Config />}
									/>
									<Route path="outlook" element={<Config />} />
									<Route path="gmail" element={<Config />} />
									<Route
										path="change_password"
										element={<Config />}
									/>
									<Route
										path="linkedin/config"
										element={<Config />}
									/>
									<Route
										path="linkedin/plugin"
										element={<Config />}
									/>
									<Route
										path="linkedin/install"
										element={<Config />}
									/>
									<Route
										path="linkedin/plugin/installation"
										element={<Config />}
									/>
									<Route
										path="linkedin/plugin/status"
										element={<Config />}
									/>
									<Route path="group" element={<Config />} />
									<Route path="vacation" element={<Config />} />
									<Route path="integrations" element={<Config />} />
									<Route path="job_ads" element={<Config />} />
									<Route path="api_keys" element={<Config />} />
								</Route>

								<Route
									path="system-settings"
									element={<SystemSettingsConfig />}
								/>

								<Route
									path="person/latest"
									element={<LatestPersonRedirect />}
								/>
								<Route
									path="person/:personId"
									element={<PersonView />}
								/>
								<Route
									path="person/:personId/:tab"
									element={<PersonView />}
								/>
								<Route
									path="person/:personId/text"
									element={<PersonTextView />}
								/>
								<Route
									path="company/:companyId"
									element={<CompanyView />}
								/>
								<Route path="job" element={<Job />} />
								<Route
									path="linkedin_job_posting"
									element={<LinkedinJobPosting />}
								/>

								{/* Charts/Analytics routes */}
								<Route path="charts">
									<Route
										path="lic_keys"
										element={<LicKeysMatrixView />}
									/>
									<Route
										path="rotating_proxies"
										element={<RotatingProxiesView />}
									/>
									<Route
										path="grade-connect-queues"
										element={<GradeConnectQueues />}
									/>
									<Route
										path="grade-connect-queues/:assignmentId"
										element={<GradeConnectQueueItems />}
									/>
									<Route
										path="assignment_charts/:assignmentId"
										element={<AssignmentCharts />}
									/>
									<Route path="bot_board" element={<BotBoard />} />
									<Route
										path="celery_tasks"
										element={<CeleryTasks />}
									/>
									<Route path="bot">
										<Route path=":botId" element={<BotView />} />
									</Route>
									<Route
										path="person_analysis"
										element={<PersonAnalysis />}
									/>
									<Route path="git_stats" element={<GitStats />} />
									<Route path="okr" element={<OkrDashboard />} />
									<Route
										path="llm_performance"
										element={<LLMPerformance />}
									/>
									<Route
										path="llm_pricing"
										element={<LLMPricing />}
									/>

									<Route
										path="data_explorer"
										element={<DataExplorer />}
									/>
									<Route
										path="human-operator-agreement"
										element={<HumanOperatorAgreement />}
									/>
									<Route
										path="llm_evaluation"
										element={<LLMEvaluation />}
									/>
									<Route
										path="llm_templates"
										element={<LLMPromptTemplates />}
									/>
									<Route
										path="extractor_prompts"
										element={<ExtractorPrompts />}
									/>
									<Route
										path="random-persons"
										element={<RandomPersons />}
									/>
									<Route
										path="latest-persons"
										element={<LatestPersons />}
									/>
									<Route path="accounts" element={<AccountsView />} />
									<Route
										path="latest-voyager-logs"
										element={<LatestVoyagerLogs />}
									/>
									<Route
										path="voyager-log-stats"
										element={<VoyagerLogStats />}
									/>
									<Route
										path="llm-cache-stats"
										element={<LLMCacheStats />}
									/>
									<Route
										path="latest-get-profile-queue"
										element={<GpqTabsWrapper />}
									/>

									<Route
										path="bot_stats"
										element={
											<StockChartView
												urlSources={[
													"/api/v1/hs/bot_ts",
													"/api/v1/hs/bot_killed_ts",
													"/api/v1/hs/odi_session_ts",
												]}
												pageTitle="Bot Stats"
											/>
										}
									/>
									<Route path="ml_stats" element={<MlStats />} />
									<Route
										path="rerank_judge"
										element={<RerankJudgeView />}
									/>
									<Route
										path="vote_stats"
										element={
											<StockChartView
												urlSources={[
													"/api/v1/hs/vote_ts?period=hour&max_period=168&vote_type=grade_connected,match_no_conversation",
													"/api/v1/hs/vote_ts?vote_type=grade_connected,match_no_conversation",
													"/api/v1/hs/vote_ts?period=week&vote_type=grade_connected,match_no_conversation",
													"/api/v1/hs/vote_ts?period=month&vote_type=grade_connected,match_no_conversation",
												]}
												pageTitle="Vote Stats"
											/>
										}
									/>

									<Route
										path="userscraping_stats"
										element={
											<StockChartView
												urlSources={[
													"/api/v1/hs/conversation_ts",
													"/api/v1/hs/sent_message_ts",
													"/api/v1/hs/sent_email_ts",
													"/api/v1/hs/ccontent_found_ts",
													"/api/v1/hs/ccontent_ts",
													"/api/v1/hs/emailjournal_created_ts",
													"/api/v1/hs/emailjournal_sent_ts",
													"/api/v1/hs/email_journal_text_ts",
													"/api/v1/hs/generic_ts?period=week&model=Connection&split_by=bot__userprofile__user__username",
													"/api/v1/hs/generic_ts?field=connected&period=week&model=Connection&split_by=bot__email&filter=bot__userprofile__isnull:False",
													"/api/v1/hs/generic_ts?period=week&model=Connection&split_by=bot__email&filter=bot__userprofile__isnull:False",
												]}
												pageTitle="Userscraping Stats"
											/>
										}
									/>

									<Route
										path="job_stats"
										element={
											<StockChartView
												urlSources={[
													"/api/v1/hs/jobs_vote_ts",
													"/api/v1/hs/jobs_ts",
													"/api/v1/hs/job_listing_ts",
													"/api/v1/hs/job_listing_vote_ts",
												]}
												pageTitle="Job Stats"
											/>
										}
									/>

									<Route
										path="botfactory_stats"
										element={
											<StockChartView
												urlSources={[
													"/api/v1/hs/generic_ts?period=day&model=IpAddressesClassification&filter=service:signup linkedin&split_by=classification",
													"/api/v1/hs/generic_ts?period=hour&model=IpAddressesClassification&filter=service:signup linkedin&split_by=classification&max_period_days=7",
													"/api/v1/hs/generic_ts?period=day&model=IpAddressesClassification&filter=service:create_account OutlookProvider&split_by=classification",
													"/api/v1/hs/generic_ts?period=hour&model=IpAddressesClassification&filter=service:create_account OutlookProvider&split_by=classification&max_period_days=7",
													"/api/v1/hs/generic_ts?period=day&model=IpAddressesClassification&filter=service:check_abuse&split_by=classification",
													"/api/v1/hs/generic_ts?period=hour&model=IpAddressesClassification&filter=service:check_abuse&split_by=classification&max_period_days=7",
													"/api/v1/hs/generic_ts?period=day&model=IpAddressesClassification&split_by=service",
													"/api/v1/hs/generic_ts?period=hour&model=IpAddressesClassification&split_by=service&max_period_days=7",
													"/api/v1/hs/generic_ts?period=day&model=IpAddressesClassification&filter=classification:Welcome&split_by=proxy_provider",
													"/api/v1/hs/generic_ts?period=hour&model=IpAddressesClassification&filter=classification:Welcome&split_by=proxy_provider&max_period_days=7",
													"/api/v1/hs/generic_ts?period=day&model=IpAddressesClassification&filter=classification:killed directly&split_by=proxy_provider",
													"/api/v1/hs/generic_ts?period=hour&model=IpAddressesClassification&filter=classification:killed directly&split_by=proxy_provider&max_period_days=7",
													"/api/v1/hs/generic_ts?period=day&model=IpAddressesClassification&filter=classification:Enable forwarding&split_by=proxy_provider",
													"/api/v1/hs/generic_ts?period=hour&model=IpAddressesClassification&filter=classification:Enable forwarding&split_by=proxy_provider&max_period_days=7",
													"/api/v1/hs/generic_ts?period=hour&model=IpAddressesClassification&split_by=proxy_provider&max_period_days=7",
													"/api/v1/hs/ipac_ts?period=hour&max_period=168",
													"/api/v1/hs/ipac_ts",
												]}
												pageTitle="Botfactory Stats"
											/>
										}
									/>

									<Route
										path="third_party_usage"
										element={
											<StockChartView
												urlSources={[
													"/api/v1/hs/generic_ts?period=day&model=OpenStreetMapsDetail",
													"/api/v1/hs/generic_ts?period=month&model=OpenStreetMapsDetail",
													"/api/v1/hs/generic_ts?period=day&model=GmapsGeoCodeCache&split_by=source",
													"/api/v1/hs/generic_ts?period=month&model=GmapsGeoCodeCache&split_by=source",
													"/api/v1/hs/generic_ts?period=day&model=GeoMapping",
													"/api/v1/hs/generic_ts?period=month&model=GeoMapping",
													"/api/v1/hs/generic_ts?period=day&model=GeoNamesCache",
													"/api/v1/hs/generic_ts?period=month&model=GeoNamesCache",
													"/api/v1/hs/generic_ts?period=day&model=RocketReachCache",
													"/api/v1/hs/generic_ts?period=month&model=RocketReachCache",
													"/api/v1/hs/generic_ts?period=day&model=SnovCache",
													"/api/v1/hs/generic_ts?period=month&model=SnovCache",
													"/api/v1/hs/generic_ts?period=day&model=DropcontactCache",
													"/api/v1/hs/generic_ts?period=month&model=DropcontactCache",
													"/api/v1/hs/generic_ts?period=day&model=SesDeliveryEvent&split_by=status",
													"/api/v1/hs/generic_ts?period=month&model=SesDeliveryEvent&split_by=status",
												]}
												pageTitle="Third Party Usage"
											/>
										}
									/>

									<Route
										path="thumb_stats"
										element={
											<>
												<StockChartView
													urlSources={[
														"/api/v1/hs/vote_ratio_morpheus_ts?timeline=True&good_vote=5",
														"/api/v1/hs/vote_ratio_morpheus_ts?timeline=True&good_vote=4",
														"/api/v1/hs/vote_ratio_ts",
														"/api/v1/hs/vote_ratio_ts_by_queue?timeline=True",
													]}
													pageTitle="Screening Stats"
												/>
												<ChartView
													urlSources={[
														"/api/v1/hs/vote_ratio_morpheus_ts?timeline=False&good_vote=5",
														"/api/v1/hs/vote_ratio_morpheus_ts?timeline=False&good_vote=4",
														"/api/v1/hs/vote_ratio_ts?timeline=False",
														"/api/v1/hs/vote_ratio_ts_by_queue?timeline=False",
													]}
													pageTitle="Screening Stats"
												/>
											</>
										}
									/>

									<Route
										path="email_stats"
										element={
											<>
												<ChartView
													urlSources={[
														"/api/v1/hs/email_openratio",
													]}
													pageTitle="Email Open Ratio"
												/>
												<ChartView
													urlSources={[
														"/api/v1/hs/email_tracking_info_per_domain",
													]}
													pageTitle="Email Tracking Info per Domain"
												/>
												<ChartView
													urlSources={[
														"/api/v1/hs/email_tracking_info",
													]}
													pageTitle="Email Tracking Info"
												/>
												<ChartView
													urlSources={[
														"/api/v1/hs/email_tracking_timing?period=second",
													]}
													pageTitle="Email Tracking Timing"
												/>
												<ChartView
													urlSources={[
														"/api/v1/hs/email_tracking_timing?period=minute",
													]}
													pageTitle="Email Tracking Timing"
												/>
												<ChartView
													urlSources={[
														"/api/v1/hs/email_tracking_timing?period=hour",
													]}
													pageTitle="Email Tracking Timing"
												/>
												<ChartView
													urlSources={[
														"/api/v1/hs/email_tracking_timing?period=day",
													]}
													pageTitle="Email Tracking Timing"
												/>
												<ChartView
													urlSources={[
														"/api/v1/hs/email_tracking_timing?period=week",
													]}
													pageTitle="Email Tracking Timing"
												/>
												<ChartView
													urlSources={[
														"/api/v1/hs/email_tracking_timing?period=month",
													]}
													pageTitle="Email Tracking Timing"
												/>
												<ChartView
													urlSources={[
														"/api/v1/hs/email_tracking_timing?period=year",
													]}
													pageTitle="Email Tracking Timing"
												/>
											</>
										}
									/>

									<Route
										path="connect_stats"
										element={
											<>
												<ChartView
													urlSources={[
														"/api/v1/hs/connect_ratio_by_type",
													]}
													pageTitle="Connect Ratio by type ALL bots"
												/>
												<ChartView
													urlSources={[
														"/api/v1/hs/connect_ratio_by_type?split_by_bot=True&invite_type=auto_invite_loop_upvoted",
													]}
													pageTitle="Connect Ratio by type ALL bots"
												/>
												<ChartView
													urlSources={[
														"/api/v1/hs/connect_ratio",
													]}
													pageTitle="Connect Ratio ALL"
												/>
												<ChartView
													urlSources={[
														"/api/v1/hs/connect_ratio?split_by_years=True",
													]}
													pageTitle="Connect Ratio ALL"
												/>
												<ChartView
													urlSources={[
														"/api/v1/hs/connect_ratio?split_by_years=True&invite_path=True",
													]}
													pageTitle="Connect Ratio ALL"
												/>
												<ChartView
													urlSources={[
														"/api/v1/hs/connect_ratio?invite_path=True",
													]}
													pageTitle="Connect Ratio ALL"
												/>
												<ChartView
													urlSources={[
														"/api/v1/hs/connect_ratio?invite_path=True&max_path=2&min_path=2",
													]}
													pageTitle="Connect Ratio ALL"
												/>
												<ChartView
													urlSources={[
														"/api/v1/hs/connect_ratio?split_by_years=True&invite_path=True&max_path=2&min_path=2",
													]}
													pageTitle="Connect Ratio ALL"
												/>

												<ChartView
													urlSources={[
														"/api/v1/hs/connect_ratio?username=jimmy",
													]}
													pageTitle="Connect Ratio jimmy"
												/>
												<ChartView
													urlSources={[
														"/api/v1/hs/connect_ratio?username=maryam",
													]}
													pageTitle="Connect Ratio maryam"
												/>
												<ChartView
													urlSources={[
														"/api/v1/hs/connect_ratio?username=ulf",
													]}
													pageTitle="Connect Ratio ulf"
												/>
												<ChartView
													urlSources={[
														"/api/v1/hs/connect_ratio?username=gustav",
													]}
													pageTitle="Connect Ratio gustav"
												/>
												<ChartView
													urlSources={[
														"/api/v1/hs/connect_ratio?username=farid",
													]}
													pageTitle="Connect Ratio farid"
												/>

												<ChartView
													urlSources={[
														"/api/v1/hs/connect_ratio?username=jimmy&split_by_years=True",
													]}
													pageTitle="Connect Ratio jimmy"
												/>
												<ChartView
													urlSources={[
														"/api/v1/hs/connect_ratio?username=maryam&split_by_years=True",
													]}
													pageTitle="Connect Ratio maryam"
												/>
												<ChartView
													urlSources={[
														"/api/v1/hs/connect_ratio?username=ulf&split_by_years=True",
													]}
													pageTitle="Connect Ratio ulf"
												/>
												<ChartView
													urlSources={[
														"/api/v1/hs/connect_ratio?username=gustav&split_by_years=True",
													]}
													pageTitle="Connect Ratio gustav"
												/>
												<ChartView
													urlSources={[
														"/api/v1/hs/connect_ratio?username=farid&split_by_years=True",
													]}
													pageTitle="Connect Ratio farid"
												/>

												<ChartView
													urlSources={[
														"/api/v1/hs/connect_ratio?username=jimmy&split_by_years=True&invite_path=True",
													]}
													pageTitle="Connect Ratio jimmy"
												/>
												<ChartView
													urlSources={[
														"/api/v1/hs/connect_ratio?username=maryam&split_by_years=True&invite_path=True",
													]}
													pageTitle="Connect Ratio maryam"
												/>
												<ChartView
													urlSources={[
														"/api/v1/hs/connect_ratio?username=ulf&split_by_years=True&invite_path=True",
													]}
													pageTitle="Connect Ratio ulf"
												/>
												<ChartView
													urlSources={[
														"/api/v1/hs/connect_ratio?username=gustav&split_by_years=True&invite_path=True",
													]}
													pageTitle="Connect Ratio gustav"
												/>
												<ChartView
													urlSources={[
														"/api/v1/hs/connect_ratio?username=farid&split_by_years=True&invite_path=True",
													]}
													pageTitle="Connect Ratio farid"
												/>

												<ChartView
													urlSources={[
														"/api/v1/hs/connect_ratio?username=jimmy&invite_path=True",
													]}
													pageTitle="Connect Ratio jimmy"
												/>
												<ChartView
													urlSources={[
														"/api/v1/hs/connect_ratio?username=maryam&invite_path=True",
													]}
													pageTitle="Connect Ratio maryam"
												/>
												<ChartView
													urlSources={[
														"/api/v1/hs/connect_ratio?username=ulf&invite_path=True",
													]}
													pageTitle="Connect Ratio ulf"
												/>
												<ChartView
													urlSources={[
														"/api/v1/hs/connect_ratio?username=gustav&invite_path=True",
													]}
													pageTitle="Connect Ratio gustav"
												/>
												<ChartView
													urlSources={[
														"/api/v1/hs/connect_ratio?username=farid&invite_path=True",
													]}
													pageTitle="Connect Ratio farid"
												/>

												<ChartView
													urlSources={[
														"/api/v1/hs/connect_ratio?username=jimmy&invite_path=True&max_path=2",
													]}
													pageTitle="Connect Ratio jimmy"
												/>
												<ChartView
													urlSources={[
														"/api/v1/hs/connect_ratio?username=maryam&invite_path=True&max_path=2",
													]}
													pageTitle="Connect Ratio maryam"
												/>
												<ChartView
													urlSources={[
														"/api/v1/hs/connect_ratio?username=ulf&invite_path=True&max_path=2",
													]}
													pageTitle="Connect Ratio ulf"
												/>
												<ChartView
													urlSources={[
														"/api/v1/hs/connect_ratio?username=gustav&invite_path=True&max_path=2",
													]}
													pageTitle="Connect Ratio gustav"
												/>
												<ChartView
													urlSources={[
														"/api/v1/hs/connect_ratio?username=farid&invite_path=True&max_path=2",
													]}
													pageTitle="Connect Ratio farid"
												/>
											</>
										}
									/>

									<Route
										path="pitch_stats"
										element={
											<StockChartView
												urlSources={[
													"/api/v1/hs/invite_analytics?username=gustav",
													"/api/v1/hs/invite_analytics?username=jimmy",
													"/api/v1/hs/invite_analytics?username=maryam",
													"/api/v1/hs/invite_analytics?username=arnaldur",
													"/api/v1/hs/sent_linkedin_pitches?username=gustav",
													"/api/v1/hs/introductions?username=gustav",
													"/api/v1/hs/linkedin_pitch_responses?username=gustav",
													"/api/v1/hs/email_pitches?username=gustav",
													"/api/v1/hs/email_pitch_responses?username=gustav",
													"/api/v1/hs/vote_ts?vote_type=match_no_conversation&split_by=assignment__name&grade=up",
													"/api/v1/hs/sent_email_ts?split_by=from_template__name",
													"/api/v1/hs/sent_email_by_pitch_ts?split_by=from_template__name",
													"/api/v1/hs/sent_linkedin_by_pitch_ts?split_by=from_template__name",
													"/api/v1/hs/sent_linkedin_by_pitch_ts?split_by=user__username",
												]}
												pageTitle="Introduction statistics"
											/>
										}
									/>

									<Route
										path="database_load"
										element={<DatabaseLoad />}
									/>
									<Route
										path="advisory_locks"
										element={<AdvisoryLocks />}
									/>
									<Route
										path="index_health"
										element={<IndexHealthView />}
									/>
									<Route
										path="database_size"
										element={<DatabaseSize />}
									/>
									<Route
										path="hot_database_tables"
										element={<HotDatabaseTables />}
									/>
									<Route path="db_backups" element={<DbBackups />} />
									<Route
										path="send_invite_queue"
										element={<SendInviteQueue />}
									/>
									<Route
										path="inmail_cost_queue"
										element={<InmailCostQueue />}
									/>
									<Route
										path="contactinfo_tng_queue"
										element={<ContactInfoTngQueue />}
									/>
									<Route path="kpis" element={<KpisPage />} />
									<Route path="loki_logs" element={<LokiLogs />} />
									<Route
										path="pydoll-monitor"
										element={<PydollScreenshotMonitor />}
									/>
									<Route
										path="captcha-monitor"
										element={<CaptchaMonitor />}
									/>
									<Route
										path="email_journal"
										element={<EmailJournalView />}
									/>
									<Route
										path="postfix_delivery_events"
										element={<PostfixDeliveryEventsView />}
									/>
									<Route
										path="ses_dashboard"
										element={<SesDashboard />}
									/>
									<Route
										path="screen_tiles"
										element={<ScreenTilesView />}
									/>
									<Route
										path="plugin_work_log"
										element={<PluginWorkLogView />}
									/>
									<Route
										path="cookies"
										element={<CookieDebugView />}
									/>
									<Route
										path="theme_colors"
										element={<ThemeColorsView />}
									/>
									<Route
										path="insights"
										element={<AnalyticsDashboard />}
									/>
									<Route
										path="vote_testsets"
										element={<VoteTestsetList />}
									/>

									<Route
										path="llm_batches"
										element={<LLMEvaluationBatchList />}
									/>

									<Route
										path="assignment-llm-skills"
										element={<AssignmentLLMSkillsList />}
									/>
									<Route
										path="llm_cache"
										element={<LLMCacheList />}
									/>
									<Route
										path="provider_batch_jobs"
										element={<ProviderBatchJobsView />}
									/>
									<Route
										path="assignments_overview"
										element={<AssignmentsOverviewView />}
									/>
									<Route
										path="assignment-llm-agreement"
										element={<AssignmentLLMAgreement />}
									/>
									<Route path="email_sync" element={<EmailSync />} />
									<Route
										path="assignment_locations"
										element={<AssignmentLocationsView />}
									/>
									<Route
										path="infra_costs"
										element={<InfraCostsView />}
									/>
									<Route
										path="ostmd/:id?"
										element={<OstmdDetailView />}
									/>
									<Route
										path="elasticsearch_status"
										element={<ElasticsearchStatusView />}
									/>
									<Route
										path="jobylon_status"
										element={<JobylonStatusView />}
									/>
								</Route>

								{/* Fingerprint Sessions */}
								<Route
									path="fingerprint-sessions"
									element={<FingerprintSessionList />}
								/>
								<Route
									path="fingerprint-sessions/:sessionId"
									element={<FingerprintSessionDetail />}
								/>

								<Route
									path="dev/runtime-error"
									element={<RuntimeErrorTrigger />}
								/>
							</Route>
						</Routes>
					</Suspense>
				</AuthProvider>
			</HotkeysProvider>
		</NuqsAdapter>
	</RootErrorBoundary>
);

const router = createBrowserRouter([
	{ path: "*", element: <RootLayout />, errorElement: <RouteErrorBoundary /> },
]);

export const Root = () => (
	<GlobalStateProvider>
		<RootErrorBoundary>
			<RouterProvider router={router} />
		</RootErrorBoundary>
	</GlobalStateProvider>
);
